import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, CircularProgress } from "@mui/material";
import { TbArrowRight, TbCheck, TbClock, TbCopy, TbDownload, TbExternalLink } from "react-icons/tb";
import { MdClose } from "react-icons/md";
import { labelById } from "../../../Components/Molecules/GridHistory/gridFilterUtils";
import { useToast } from "../../../Hooks/toast";
import { fetchStablesHistoryDetail } from "../../../Hooks/useStablesHistory";
import { mapHistoryItemForDetailDisplay, shouldShowOnChainProof } from "../../../Services/stablesApi";
import {
  formatBrl,
  formatStable,
  getAssetLabel,
  resolveWithdrawalPlatformFee,
} from "./constants";
import { formatQuotePrice } from "./stablesQuoteDisplay";
import { downloadStablesReceipt, getReceiptUrl } from "./downloadStablesReceipt";
import {
  getHistoryItemPaymentMethodId,
  getStablesHistoryPaymentMethods,
} from "./stablesHistoryFilters";

const OPERATION_LABELS = {
  buy: "Compra",
  sell: "Venda",
  swap: "Conversão",
  withdraw: "Saque",
};

const isProcessingStatus = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  return normalized.includes("process") || normalized === "em_processamento";
};

const formatStatusLabel = (status) => {
  if (!status) return "—";
  if (isProcessingStatus(status)) return "Em processamento";
  if (String(status).toLowerCase().includes("conclu")) return "Concluída";
  return status;
};

const shortenHash = (hash) => {
  if (!hash || hash.length < 12) return hash;
  return `${hash.slice(0, 10)}…${hash.slice(-8)}`;
};

const DetailCard = ({ title, children, className = "" }) => (
  <section
    className={`rounded-xl border border-gray-100 bg-gray-50/60 p-4 dark:border-brand-dark-border dark:bg-brand-dark-elevated/60 ${className}`}
  >
    {title && (
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {title}
      </h3>
    )}
    {children}
  </section>
);

const MetaCell = ({ label, value, mono, span }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</p>
    <p
      className={`mt-1 text-sm font-semibold text-brand-dark dark:text-gray-100 ${
        mono ? "break-all font-mono text-xs" : ""
      }`}
    >
      {value || "—"}
    </p>
  </div>
);

const AmountLine = ({ label, value, primary }) => (
  <div className={`flex items-center justify-between gap-3 ${primary ? "py-1" : "py-0.5"}`}>
    <span className={`text-sm ${primary ? "font-medium text-gray-600 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"}`}>
      {label}
    </span>
    <span
      className={`text-right font-bold ${
        primary
          ? "text-lg text-brand-dark dark:text-gray-50"
          : "text-sm text-gray-700 dark:text-gray-200"
      }`}
    >
      {value || "—"}
    </span>
  </div>
);

const FeeLine = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-gray-400 dark:text-gray-500">{label}</span>
    <span className="text-right text-xs text-gray-400 dark:text-gray-500">{value}</span>
  </div>
);

const FeesInline = ({ detail, fees }) => {
  const hasFees = fees.feePercent > 0 || fees.networkFeeStable > 0;
  if (!hasFees) return null;

  return (
    <div className="mt-2 space-y-1 border-t border-gray-100 pt-2 dark:border-brand-dark-border/60">
      {fees.feePercent > 0 && (
        <FeeLine
          label={`Taxa (${fees.feePercent}%)`}
          value={
            detail.operation === "swap"
              ? formatStable(fees.feeStable, detail.toAssetId || detail.assetId)
              : formatBrl(fees.feeBrl)
          }
        />
      )}
      {fees.networkFeeStable > 0 && (
        <FeeLine
          label="Taxa da rede"
          value={formatStable(fees.networkFeeStable, detail.assetId)}
        />
      )}
    </div>
  );
};

const DetailHero = ({ isProcessing, operationLabel, statusLabel }) => (
  <div className="flex flex-col items-center pb-1 pt-2 text-center">
    <span
      className={`inline-flex h-16 w-16 items-center justify-center rounded-full ring-4 ${
        isProcessing
          ? "bg-amber-50 text-amber-600 ring-amber-100 dark:bg-amber-950/50 dark:text-amber-400 dark:ring-amber-900/40"
          : "bg-emerald-50 text-emerald-600 ring-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-400 dark:ring-emerald-900/40"
      }`}
    >
      {isProcessing ? <TbClock className="h-8 w-8" /> : <TbCheck className="h-8 w-8" strokeWidth={2.5} />}
    </span>
    <p className="mt-4 text-lg font-bold text-brand-dark dark:text-gray-100">{operationLabel}</p>
    <span
      className={`mt-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
        isProcessing
          ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
          : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
      }`}
    >
      {statusLabel}
    </span>
  </div>
);

const resolvePaymentMethodLabel = (detail, listItem) => {
  if (detail.paymentMethod) return detail.paymentMethod;

  const paymentMethodId = detail.paymentMethodId ?? getHistoryItemPaymentMethodId(listItem ?? detail);
  if (!paymentMethodId) return null;

  return labelById(getStablesHistoryPaymentMethods(), paymentMethodId);
};

const resolveDisplayFees = (detail, listItem) => {
  const operation = detail.operation;
  const feePercent = Number(detail.feePercent) || 0;

  if (operation === "buy" && !detail.feeBrl && detail.brlAmount > 0 && feePercent > 0) {
    const feeBrl = detail.brlAmount * (feePercent / 100);
    return { feePercent, feeBrl, feeStable: detail.feeStable };
  }

  if (operation === "sell" && !detail.feeBrl && detail.netBrlAmount > 0 && feePercent > 0) {
    const feeBrl = detail.netBrlAmount * (feePercent / 100);
    return { feePercent, feeBrl, feeStable: detail.feeStable };
  }

  if (operation === "swap" && !detail.feeStable && detail.netToAmount > 0 && feePercent > 0) {
    const feeStable = detail.netToAmount * (feePercent / 100);
    return { feePercent, feeBrl: detail.feeBrl, feeStable };
  }

  if (operation === "withdraw" && !detail.feeStable) {
    const feeStable = resolveWithdrawalPlatformFee(listItem ?? detail);
    return { feePercent, feeBrl: detail.feeBrl, feeStable, networkFeeStable: detail.networkFeeStable };
  }

  return {
    feePercent: detail.feePercent,
    feeBrl: detail.feeBrl,
    feeStable: detail.feeStable,
    networkFeeStable: detail.networkFeeStable,
  };
};

const buildReceiptDownloadItem = (detail) => ({
  id: detail.transactionId,
  receiptUrl: getReceiptUrl(detail),
});

const OnChainHashCompact = ({ detail, isProcessing, onCopy }) => {
  if (!detail.txHash) {
    const pendingMessage =
      detail.operation === "withdraw" && isProcessing
        ? "O hash on-chain será gerado após o processamento do saque."
        : "Transação ainda não confirmada on-chain.";

    return (
      <div className="sm:col-span-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Transaction hash
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{pendingMessage}</p>
      </div>
    );
  }

  return (
    <div className="sm:col-span-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Transaction hash
      </p>
      <div className="mt-1 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-brand-dark dark:text-gray-100">
          {shortenHash(detail.txHash)}
        </code>
        <button
          type="button"
          className="btn-outline-icon !shrink-0 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100 dark:hover:border-brand-gold-mid/40 dark:hover:bg-[#1f1f1f]"
          onClick={onCopy}
          title="Copiar hash"
        >
          <TbCopy className="h-4 w-4 dark:text-gray-100" />
        </button>
      </div>
    </div>
  );
};

const WithdrawConsolidatedCard = ({
  detail,
  statusLabel,
  paymentMethod,
  fees,
  showOnChainProof,
  isProcessing,
  onCopyHash,
}) => (
  <DetailCard title="Detalhes da transação">
    <div className="grid gap-3 sm:grid-cols-2">
      <MetaCell label="ID da transação" value={detail.transactionId} mono span />
      <MetaCell label="Data" value={detail.date} />
      <MetaCell label="Status" value={statusLabel} />
      {paymentMethod && <MetaCell label="Forma de pagamento" value={paymentMethod} />}

      <div className="space-y-0.5 sm:col-span-2 sm:border-t sm:border-gray-100 sm:pt-3 dark:sm:border-brand-dark-border/60">
        <AmountLine label="Valor sacado" value={formatStable(detail.stableAmount, detail.assetId)} />
        <AmountLine
          label="Valor líquido"
          value={formatStable(detail.netStableAmount, detail.assetId)}
          primary
        />
        <FeesInline detail={detail} fees={fees} />
      </div>

      {detail.destination && <MetaCell label="Destino" value={detail.destination} mono span />}

      {showOnChainProof && (
        <>
          {detail.chain && <MetaCell label="Rede (chain)" value={detail.chain} />}
          <OnChainHashCompact detail={detail} isProcessing={isProcessing} onCopy={onCopyHash} />
        </>
      )}
    </div>
  </DetailCard>
);

export const StablesHistoryDetailModal = ({ open, onClose, item }) => {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fallbackDetail = useMemo(
    () => (item ? mapHistoryItemForDetailDisplay(item) : null),
    [item]
  );

  useEffect(() => {
    if (!open || !item?.id) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setDetail(mapHistoryItemForDetailDisplay(item));

    fetchStablesHistoryDetail(item.id, item)
      .then((result) => {
        if (!cancelled) setDetail(result ?? mapHistoryItemForDetailDisplay(item));
      })
      .catch(() => {
        if (!cancelled) setDetail(mapHistoryItemForDetailDisplay(item));
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, item]);

  if (!item) return null;

  const displayDetail = detail ?? fallbackDetail;
  if (!displayDetail) return null;

  const operationLabel = OPERATION_LABELS[displayDetail.operation] || "Transação";
  const paymentMethod = resolvePaymentMethodLabel(displayDetail, item);
  const fees = resolveDisplayFees(displayDetail, item);
  const receiptUrl = getReceiptUrl(displayDetail);
  const hasReceipt = Boolean(receiptUrl);
  const isProcessing = isProcessingStatus(displayDetail.status);
  const statusLabel = formatStatusLabel(displayDetail.status);
  const showOnChainProof = shouldShowOnChainProof({
    operation: displayDetail.operation,
    hasOnChainProof: displayDetail.hasOnChainProof,
    txHash: displayDetail.txHash,
  });

  const handleCopyHash = async () => {
    if (!displayDetail.txHash) return;

    try {
      await navigator.clipboard.writeText(displayDetail.txHash);
      toast.addToast({
        type: "success",
        title: "Hash copiado",
        description: "Transaction hash copiado para a área de transferência.",
      });
    } catch {
      toast.addToast({ type: "error", title: "Erro ao copiar", description: "Não foi possível copiar o hash." });
    }
  };

  const handleDownloadReceipt = async () => {
    if (!hasReceipt) return;

    setDownloading(true);

    try {
      await downloadStablesReceipt(buildReceiptDownloadItem(displayDetail));
      toast.addToast({
        type: "success",
        title: "Comprovante baixado",
        description: "O PDF foi salvo com sucesso.",
      });
    } catch (error) {
      const isMissing = error?.message === "RECEIPT_URL_MISSING";
      toast.addToast({
        type: "error",
        title: "Erro ao baixar",
        description: isMissing
          ? "Comprovante ainda não disponível para esta transação."
          : "Não foi possível baixar o comprovante. Tente novamente.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const amountCard = () => {
    if (displayDetail.operation === "buy") {
      return (
        <DetailCard title="Valores">
          <AmountLine label="Valor pago (BRL)" value={formatBrl(displayDetail.brlAmount)} />
          <div className="my-3 border-t border-[#e7a73f]/20 dark:border-brand-gold-mid/20" />
          <AmountLine
            label="Stablecoin recebida"
            value={formatStable(displayDetail.netStableAmount, displayDetail.assetId)}
            primary
          />
          <FeesInline detail={displayDetail} fees={fees} />
        </DetailCard>
      );
    }

    if (displayDetail.operation === "sell") {
      return (
        <DetailCard title="Valores">
          <AmountLine
            label="Stablecoin vendida"
            value={formatStable(displayDetail.stableAmount, displayDetail.assetId)}
          />
          <div className="my-3 border-t border-[#e7a73f]/20 dark:border-brand-gold-mid/20" />
          <AmountLine label="Valor creditado (BRL)" value={formatBrl(displayDetail.netBrlAmount)} primary />
          <FeesInline detail={displayDetail} fees={fees} />
        </DetailCard>
      );
    }

    if (displayDetail.operation === "swap") {
      const fromLabel =
        displayDetail.fromAssetLabel || getAssetLabel(displayDetail.fromAssetId) || "—";
      const toLabel =
        displayDetail.toAssetLabel || getAssetLabel(displayDetail.toAssetId) || "—";
      const sent = formatStable(displayDetail.fromAmount, displayDetail.fromAssetId);
      const received = formatStable(displayDetail.netToAmount, displayDetail.toAssetId);
      return (
        <DetailCard title="Valores">
          <p className="mb-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
            Origem <span className="font-semibold text-brand-dark dark:text-gray-200">{fromLabel}</span>
            {" → "}
            Destino <span className="font-semibold text-brand-dark dark:text-gray-200">{toLabel}</span>
          </p>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1 text-center">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Enviado</p>
              <p className="mt-1 truncate text-sm font-semibold text-gray-700 dark:text-gray-200">{sent}</p>
            </div>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e7a73f]/15 text-[#c8872a] dark:bg-brand-gold-mid/15 dark:text-brand-gold-mid">
              <TbArrowRight className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1 text-center">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Recebido</p>
              <p className="mt-1 truncate text-base font-bold text-brand-dark dark:text-gray-50">{received}</p>
            </div>
          </div>
          <FeesInline detail={displayDetail} fees={fees} />
        </DetailCard>
      );
    }

    if (displayDetail.operation === "withdraw") {
      return (
        <DetailCard title="Valores">
          <AmountLine
            label="Valor sacado"
            value={formatStable(displayDetail.stableAmount, displayDetail.assetId)}
          />
          <div className="my-3 border-t border-[#e7a73f]/20 dark:border-brand-gold-mid/20" />
          <AmountLine
            label="Valor líquido"
            value={formatStable(displayDetail.netStableAmount, displayDetail.assetId)}
            primary
          />
          <FeesInline detail={displayDetail} fees={fees} />
          {displayDetail.destination && (
            <>
              <div className="my-3 border-t border-gray-200/80 dark:border-brand-dark-border" />
              <MetaCell label="Destino" value={displayDetail.destination} mono />
            </>
          )}
        </DetailCard>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="!relative !pr-12 !text-center !text-sm !font-semibold !text-gray-500 dark:!text-gray-400">
        Detalhes da transação
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          size="small"
          className="!absolute !right-2 !top-2 dark:!text-gray-300"
        >
          <MdClose />
        </IconButton>
      </DialogTitle>

      <DialogContent className="!pt-0">
        {loadingDetail && !detail ? (
          <div className="flex justify-center py-12">
            <CircularProgress size={28} className="!text-[#e7a73f]" />
          </div>
        ) : (
          <div className="space-y-4">
            <DetailHero
              isProcessing={isProcessing}
              operationLabel={operationLabel}
              statusLabel={statusLabel}
            />

            {displayDetail.operation === "withdraw" ? (
              <WithdrawConsolidatedCard
                detail={displayDetail}
                statusLabel={statusLabel}
                paymentMethod={paymentMethod}
                fees={fees}
                showOnChainProof={showOnChainProof}
                isProcessing={isProcessing}
                onCopyHash={handleCopyHash}
              />
            ) : (
              <>
                <DetailCard title="Detalhes da transação">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <MetaCell label="ID da transação" value={displayDetail.transactionId} mono span />
                    <MetaCell label="Data" value={displayDetail.date} />
                    <MetaCell label="Status" value={statusLabel} />
                    {paymentMethod && <MetaCell label="Forma de pagamento" value={paymentMethod} />}
                  </div>
                </DetailCard>

                {amountCard()}

                {displayDetail.rate > 0 && displayDetail.operation !== "swap" && (
                  <DetailCard>
                    <AmountLine
                      label="Cotação"
                      value={`1 ${displayDetail.assetLabel || getAssetLabel(displayDetail.assetId)} = ${formatQuotePrice(displayDetail.rate)}`}
                    />
                  </DetailCard>
                )}

                {showOnChainProof && (
                  <DetailCard title="Comprovação on-chain">
                    {displayDetail.chain && <MetaCell label="Rede (chain)" value={displayDetail.chain} />}

                    <div className={displayDetail.chain ? "mt-4" : ""}>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Transaction hash
                      </p>
                      {displayDetail.txHash ? (
                        <div className="mt-2 flex items-start gap-2">
                          <code className="stables-code-block min-w-0 flex-1 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100">
                            {displayDetail.txHash}
                          </code>
                          <button
                            type="button"
                            className="btn-outline-icon !shrink-0 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100 dark:hover:border-brand-gold-mid/40 dark:hover:bg-[#1f1f1f]"
                            onClick={handleCopyHash}
                            title="Copiar hash"
                          >
                            <TbCopy className="h-4 w-4 dark:text-gray-100" />
                          </button>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          Transação ainda não confirmada on-chain.
                        </p>
                      )}
                    </div>

                    {displayDetail.explorerUrl && (
                      <div className="mt-4">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                          Explorador da chain
                        </p>
                        <a
                          href={displayDetail.explorerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-no-zoom mt-2 inline-flex max-w-full items-center gap-1.5 text-sm font-semibold text-brand-dark hover:opacity-70 dark:text-gray-100"
                        >
                          <TbExternalLink className="h-4 w-4 shrink-0" />
                          <span className="truncate">{displayDetail.explorerUrl}</span>
                        </a>
                        {displayDetail.txHash && (
                          <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-300">
                            Resumo: {shortenHash(displayDetail.txHash)} em {displayDetail.chain}
                          </p>
                        )}
                      </div>
                    )}
                  </DetailCard>
                )}
              </>
            )}

            {!hasReceipt && (
              <p className="text-center text-xs text-gray-400 dark:text-gray-300">
                O comprovante em PDF ficará disponível quando a API retornar a URL do documento.
              </p>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions className="!flex !flex-wrap !justify-end !gap-2 !border-t !border-gray-100 !px-6 !pb-4 !pt-3 dark:!border-brand-dark-border">
        <button
          type="button"
          className="btn-outline dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100 dark:hover:bg-[#1f1f1f] disabled:dark:opacity-50"
          onClick={handleDownloadReceipt}
          disabled={downloading || !hasReceipt || loadingDetail}
          title={!hasReceipt ? "Comprovante indisponível" : undefined}
        >
          <TbDownload className="h-4 w-4" />
          {downloading ? "Baixando..." : "Baixar comprovante"}
        </button>

        {showOnChainProof && displayDetail.explorerUrl ? (
          <a
            href={displayDetail.explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary no-underline"
          >
            <TbExternalLink className="h-4 w-4" />
            Abrir na blockchain
          </a>
        ) : (
          <button type="button" className="btn-primary" onClick={onClose}>
            Fechar
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};
